import React, { useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { OrderLineTypes } from "@repo/database";
import { generateRandomString } from "@repo/utils";
import { addMilliseconds } from "date-fns";
import { PlusIcon, TagIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { CartItem } from "~/providers/cart-provider";
import { useCourse } from "~/hooks/use-course";
import { useCartManager } from "~/managers/use-cart-manager";
import { useCartStore } from "~/stores/use-cart-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { MemoizedKeypadInput } from "../ui/keypad-input";
import { Label } from "../ui/label";
import { RegisterButton } from "../ui/register-button";
import CartItemCommentForm from "./cart-item-comment-form";
import CartItemDiscountForm from "./cart-item-discount-form";

export default function CartItemContext({
  item,
  onClose,
}: {
  item: CartItem;
  onClose: () => void;
}) {
  const { courses } = useCourse();

  const { t } = useTranslation("common");

  const [amountOpen, setAmountOpen] = useState(false);

  const { items, setExtraDialogOpen } = useCartStore();

  const { removeFromCart, addToCart } = useCartManager();

  const handleRemove = async () => {
    await removeFromCart(item, 1);
    onClose();
  };

  const handleAdd = async (amount: number) => {
    const children = items.filter((i) => i.parentReference === item.reference);

    for (let i = 0; i < amount; i++) {
      const newItemReference = generateRandomString(8);

      await addToCart({
        ...item,
        ordered: false,
        printed: false,
        createdAt: new Date(),
        quantity: 1,
        originalQuantity: 1,
        reference: newItemReference,
      });

      for (const [index, child] of children.entries()) {
        await addToCart({
          ...child,
          ordered: false,
          printed: false,
          createdAt: addMilliseconds(new Date(), index + 1),
          quantity: 1,
          originalQuantity: 1,
          parentReference: newItemReference,
          reference: generateRandomString(8),
        });
      }
    }

    onClose();
  };

  const [commentOpen, setCommentOpen] = useState(false);

  const handleOrderChart = async (courseId: string) => {
    const result = await addToCart({
      ...item,
      courseId,
    });
    if (result === true) {
      onClose();
    }
  };

  const handleExtra = () => {
    setExtraDialogOpen(true);
    onClose();
  };

  return (
    <div className="flex flex-col gap-2">
      <Dialog open={amountOpen} onOpenChange={setAmountOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter amount to add</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <MemoizedKeypadInput
            type="number"
            value={1}
            min={1}
            max={100}
            onChange={handleAdd}
          />
        </DialogContent>
      </Dialog>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-4 flex-1">
          <Label className="text-center">{t("CartItem.options")}</Label>

          <div className="grid grid-cols-1 gap-4">
            <RegisterButton
              disabled={!!item.parentReference}
              variant="success"
              onClick={() => handleAdd(1)}
            >
              {t("Cart.add_one")}
            </RegisterButton>
            <RegisterButton
              disabled={!!item.parentReference}
              variant="outline"
              onClick={() => {
                setAmountOpen(true);
              }}
            >
              {t("Cart.add_x")}
            </RegisterButton>

            <RegisterButton
              variant="destructive"
              disabled={item.quantity <= 0}
              onClick={handleRemove}
            >
              {t("Cart.remove_one")}
            </RegisterButton>
          </div>
        </div>
        {courses.length > 0 && (
          <>
            <div className="border-r-2"></div>
            <div className="flex flex-col gap-4">
              <Label className="text-center">{t("CartItem.order_chart")}</Label>
              <div className="flex flex-col gap-4">
                {courses
                  .sort((a, b) => a.position - b.position)
                  .map((course) => (
                    <RegisterButton
                      disabled={
                        item.ordered === true ||
                        item.courseId === course.id ||
                        !!item.parentReference ||
                        item.type !== OrderLineTypes.product
                      }
                      onClick={async () => {
                        await handleOrderChart(course.id);
                      }}
                      color={course.color ?? undefined}
                      key={course.id}
                    >
                      {course.name}
                    </RegisterButton>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="border-t-2"></div>
      <div className="flex flex-col gap-4">
        <RegisterButton
          onClick={handleExtra}
          disabled={
            item.type !== OrderLineTypes.product ||
            item.ordered ||
            !!item.parentReference
          }
          className="w-full text-lg"
          variant="secondary"
        >
          <PlusIcon />
          {t("CartItem.add_extra")}
        </RegisterButton>
        <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
          <DialogTrigger asChild>
            <RegisterButton
              className="text-lg"
              variant="primary"
              disabled={
                item.ordered ||
                !!item.parentReference ||
                item.type !== OrderLineTypes.product
              }
              onClick={() => setCommentOpen(true)}
            >
              <TagIcon />
              {t("Cart.add_comment")}
            </RegisterButton>
          </DialogTrigger>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader canClose={false}>
              <DialogTitle>
                {t("Comment.title_with_item", { item: item.name })}
              </DialogTitle>
              <VisuallyHidden>
                <DialogDescription></DialogDescription>
              </VisuallyHidden>
            </DialogHeader>
            <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
              <CartItemCommentForm item={item} onClose={onClose} />
            </div>
            <DialogFooter>
              <div className="flex flex-col gap-3 w-full">
                <Button
                  size="lg"
                  type="submit"
                  form="order-line-comment-form"
                  className="w-full text-xl"
                  variant="success"
                >
                  {t("Comment.apply")}
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                  }}
                  className="w-full"
                  variant="destructive"
                >
                  {t("cancel")}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog>
          <DialogTrigger asChild>
            <RegisterButton
              disabled={item.type !== OrderLineTypes.product}
              className="w-full"
              variant="warning"
            >
              {t("CartItem.add_discount")}
            </RegisterButton>
          </DialogTrigger>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t("CartItem.add_discount")}</DialogTitle>
              <VisuallyHidden>
                <DialogDescription></DialogDescription>
              </VisuallyHidden>
            </DialogHeader>
            <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
              <CartItemDiscountForm item={item} onClose={onClose} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
